import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { isUnauthorizedApiError, rethrowIfUnauthorized } from '../api/errors'
import { resolveUserFacingErrorMessage } from '../lib/userFacingErrorMessage'
import type {
  ClientUiImageSizeOption,
  ClientUiPublic,
  PlatformConfigBundle,
} from '../api/platformConfig'
import { getPlatformConfig } from '../api/platformConfig'
import { normalizeCatalogPlatformId } from '../constants/aiPlatforms'
import { EMPTY_CLIENT_UI } from '../constants/emptyClientUi'
import {
  resolveAspectRatioDisplay,
  resolveModelDisplay,
  resolveOptionLabel,
  resolveOptionValueDisplay,
  resolveSizeDisplay,
} from '../lib/clientUiDisplayCatalog'

type PlatformConfigState = {
  /** 成功同步后为服务端清单；失败或未请求成功前为 {@link EMPTY_CLIENT_UI} */
  clientUi: ClientUiPublic
  /** `/v1/platform-configs` 的 ai.platforms（启用且已配密钥的平台用于下拉） */
  aiPlatforms: PlatformConfigBundle['ai']['platforms'] | null
  /** 是否曾成功从后端刷新 */
  remoteSynced: boolean
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

const Ctx = createContext<PlatformConfigState | null>(null)

function normalizeClientUi(raw: unknown): ClientUiPublic | null {
  if (raw == null || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const models = Array.isArray(o.imageModels) ? o.imageModels : []
  const textModelsRaw = Array.isArray(o.textModels) ? o.textModels : []
  const sizes = Array.isArray(o.imageSizeOptions) ? o.imageSizeOptions : []
  const max = o.maxImagesPerRequest

  const mapSizeRow = (
    row: Record<string, unknown>,
    platformId: string,
    modelId: string,
  ): ClientUiImageSizeOption => {
    const pw = row.previewWidth
    const ph = row.previewHeight
    const value = String(row.value ?? '')
    const display = resolveSizeDisplay(platformId, modelId, value)
    return {
      value,
      label: String(row.label ?? '') || display.label,
      description: String(row.description ?? '') || display.description,
      previewWidth: typeof pw === 'number' ? pw : 1024,
      previewHeight: typeof ph === 'number' ? ph : 1024,
    }
  }

  const mappedModels = models
    .map((m) => {
      const row = m as Record<string, unknown>
      const so = row.sizeOptions
      const ao = row.aspectRatioOptions
      const platform = row.platform
      const eo = row.extraOptions
      const id = String(row.id ?? '')
      const platformId = normalizeCatalogPlatformId(
        typeof platform === 'string' ? platform : '',
      )
      if (!platformId || !id) return null
      const modelDisplay = resolveModelDisplay(platformId, id)
      const label = String(row.label ?? '') || modelDisplay.label
      const description = String(row.description ?? '') || modelDisplay.description
      return {
        id,
        label,
        description,
        platform: platformId,
        sizeOptions: Array.isArray(so)
          ? so
              .map((x) => mapSizeRow(x as Record<string, unknown>, platformId, id))
              .filter((z) => z.value)
          : undefined,
        aspectRatioOptions: Array.isArray(ao)
          ? ao.map((x) => {
              const r = x as Record<string, unknown>
              const value = String(r.value ?? '')
              return {
                value,
                label: String(r.label ?? '') || resolveAspectRatioDisplay(value),
              }
            }).filter((z) => z.value)
          : undefined,
        supportsReferenceImages:
          row.supportsReferenceImages === true ||
          row.supports_reference_images === true,
        maxReferenceImages:
          typeof row.maxReferenceImages === 'number' ? row.maxReferenceImages : undefined,
        nMin:
          typeof row.nMin === 'number'
            ? row.nMin
            : typeof row.n_min === 'number'
              ? row.n_min
              : undefined,
        nMax:
          typeof row.nMax === 'number'
            ? row.nMax
            : typeof row.n_max === 'number'
              ? row.n_max
              : undefined,
        extraOptions: Array.isArray(eo)
          ? eo.map((x) => {
              const r = x as Record<string, unknown>
              const opts = r.options
              const optionsList = Array.isArray(opts)
                ? opts.map((o) => {
                    const opt = o as Record<string, unknown>
                    const optValue = String(opt.value ?? '')
                    const optionValueDisplay = resolveOptionValueDisplay(
                      platformId,
                      id,
                      String(r.key ?? ''),
                      optValue,
                    )
                    return {
                      value: optValue,
                      label: String(opt.label ?? '') || optionValueDisplay.label,
                      description: String(opt.description ?? '') || optionValueDisplay.description,
                    }
                  })
                : []
              const rawType = (typeof r.type === 'string' ? r.type : 'select').toLowerCase()
              const type =
                optionsList.length > 0 && rawType === 'number'
                  ? ('select' as const)
                  : rawType === 'number' || rawType === 'boolean' || rawType === 'select'
                    ? (rawType as 'select' | 'number' | 'boolean')
                    : ('select' as const)
              return {
                key: String(r.key ?? ''),
                label: String(r.label ?? '') || resolveOptionLabel(String(r.key ?? '')),
                type,
                defaultValue: r.defaultValue != null ? String(r.defaultValue) : undefined,
                options: optionsList,
              }
            }).filter((z) => z.key)
          : undefined,
        dimensionMode: (() => {
          const raw = row.dimensionMode
          if (typeof raw === 'string') {
            const v = raw.trim().toLowerCase()
            if (v === 'aspect_ratio') return 'aspect_ratio' as const
            if (v === 'size') return 'size' as const
          }
          if (Array.isArray(ao) && ao.length > 0) return 'aspect_ratio' as const
          return 'size' as const
        })(),
      }
    })
    .filter((m): m is NonNullable<typeof m> => m != null && !!m.id)

  const imageModels = mappedModels

  const textModels = textModelsRaw
    .map((m) => {
      const row = m as Record<string, unknown>
      const platform = row.platform
      const id = String(row.id ?? '')
      const platformId = normalizeCatalogPlatformId(
        typeof platform === 'string' ? platform : '',
      )
      if (!platformId || !id) return null
      const modelDisplay = resolveModelDisplay(platformId, id)
      return {
        id,
        label: modelDisplay.label,
        description: modelDisplay.description,
        platform: platformId,
      }
    })
    .filter((m): m is NonNullable<typeof m> => m != null && !!m.id)

  // 图片与文本目录相互独立：任一侧有数据即可视为有效配置
  if (imageModels.length === 0 && textModels.length === 0 && sizes.length === 0) {
    return null
  }

  return {
    maxImagesPerRequest:
      typeof max === 'number' && Number.isFinite(max) && max >= 1 ? Math.floor(max) : 1,
    textModels,
    imageModels,
    imageSizeOptions: sizes
      .map((s) => mapSizeRow(s as Record<string, unknown>, '', ''))
      .filter((z) => z.value),
    referenceSuperResolutionAvailable: o.referenceSuperResolutionAvailable === true,
  }
}

export function PlatformConfigProvider({ children }: { children: ReactNode }) {
  const [clientUi, setClientUi] = useState<ClientUiPublic>(EMPTY_CLIENT_UI)
  const [aiPlatforms, setAiPlatforms] = useState<PlatformConfigBundle['ai']['platforms'] | null>(null)
  const [remoteSynced, setRemoteSynced] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    let keepLoadingForAuthRedirect = false
    try {
      const bundle = await getPlatformConfig()
      const nu = normalizeClientUi(bundle.clientUi)
      if (nu) {
        setClientUi(nu)
        setAiPlatforms(bundle.ai?.platforms ?? null)
        setRemoteSynced(true)
        return
      }
      setRemoteSynced(false)
      setClientUi(EMPTY_CLIENT_UI)
      setAiPlatforms(null)
      setError('服务端未返回可用模型')
    } catch (e) {
      if (isUnauthorizedApiError(e)) {
        keepLoadingForAuthRedirect = true
        rethrowIfUnauthorized(e)
      }
      setRemoteSynced(false)
      setError(resolveUserFacingErrorMessage(e, '配置加载失败'))
      setClientUi(EMPTY_CLIENT_UI)
      setAiPlatforms(null)
    } finally {
      if (!keepLoadingForAuthRedirect) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const value = useMemo<PlatformConfigState>(
    () => ({
      clientUi,
      aiPlatforms,
      remoteSynced,
      loading,
      error,
      refresh,
    }),
    [clientUi, aiPlatforms, remoteSynced, loading, error, refresh],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function usePlatformConfig(): PlatformConfigState {
  const v = useContext(Ctx)
  if (!v) throw new Error('usePlatformConfig must be used within PlatformConfigProvider')
  return v
}
